import type { SnEvent } from './types'

export type ScheduleOrder = 'asc' | 'desc'

export interface FilterEventsOptions {
  /** ISO instant. Events starting before this are dropped. */
  startsAfter?: string | null
  /** ISO instant. Events starting after this are dropped. */
  startsBefore?: string | null
  order?: ScheduleOrder
}

/** ms since epoch, or null when unparseable. */
const toTime = (iso?: string | null): number | null => {
  if (!iso) {
    return null
  }
  const time = new Date(iso).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * Applies the schedule filters that an event can actually answer, then sorts.
 *
 * Only date-window and order are here, deliberately. The events endpoints take
 * no query params, so this is the whole of event filtering — and Division,
 * Location and Surface have no event-side field to match against (a roster
 * event has no division-level schedule, and `location` is free text rather than
 * a venue record). Those three are left unapplied, so events keep showing while
 * one is set.
 *
 * An event with a missing or unparseable `starts_at` is dropped rather than
 * sorted as NaN, which would scatter it unpredictably through the list.
 */
export const filterEvents = (
  events: SnEvent[] | null | undefined,
  { startsAfter, startsBefore, order = 'asc' }: FilterEventsOptions = {}
): SnEvent[] => {
  if (!events?.length) {
    return []
  }

  const after = toTime(startsAfter)
  const before = toTime(startsBefore)

  const withTime = events
    .map((event) => ({ event, time: toTime(event?.starts_at) }))
    .filter(
      (entry): entry is { event: SnEvent; time: number } => entry.time !== null
    )
    .filter(({ time }) => {
      if (after !== null && time < after) {
        return false
      }
      if (before !== null && time > before) {
        return false
      }
      return true
    })

  withTime.sort((a, b) =>
    order === 'desc' ? b.time - a.time : a.time - b.time
  )

  return withTime.map(({ event }) => event)
}
